import React from "react";

import { useGetMyCardsQuery } from "../api/respectCardsApi";
import type { CardInstance, Playability } from "../types/cardTypes";
import PlayCardModal from "../components/PlayCardModal";

/**
 * A single Play modal shared by every card affordance on the AOTD page — the
 * hand row in AotdCardsPanel and the Anonymous-card hint above the comment box.
 * Both surfaces call the same `openCardPlay`, so there is one modal, one
 * `aotdId` play context, and one place that owns the play lifecycle.
 *
 * The provider also owns the page's `/my-cards` read (RTK dedupes it with
 * HandStrip's own subscription) and derives the in-hand Anonymous card from it,
 * so AotdComments no longer needs a private hand query just for its hint.
 */
interface AotdCardPlayContextValue {
  /**
   * Open the Play modal for a specific in-hand copy. `playability` (from the
   * tile) drives the pre-flight block/deadline and the target pool; omit it for
   * a raw instance (the comment hint) and the modal falls back to a plain
   * "ready" flow.
   */
  openCardPlay: (card: CardInstance, playability?: Playability | null) => void;
  /** In-hand Anonymous card (comment_anonymize/anonymize), for the comments hint. */
  anonymousCardInHand: CardInstance | null;
  /**
   * Ask the hand to lift its first card that can be played now (the waiting
   * screens' cards button). The hand watches `playableFocusRequest`.
   */
  focusPlayableCard: () => void;
  /** Goes up by one on every `focusPlayableCard`. */
  playableFocusRequest: number;
}

const AotdCardPlayContext = React.createContext<AotdCardPlayContextValue | null>(
  null,
);

const INERT: AotdCardPlayContextValue = {
  openCardPlay: () => {},
  anonymousCardInHand: null,
  focusPlayableCard: () => {},
  playableFocusRequest: 0,
};

/**
 * Returns the shared opener, or an inert value when rendered outside the
 * provider (e.g. an AOTD sub-component reused elsewhere) so consumers never
 * need a null check before wiring a click handler.
 */
export function useAotdCardPlay(): AotdCardPlayContextValue {
  const ctx = React.useContext(AotdCardPlayContext);
  return ctx ?? INERT;
}

interface AotdCardPlayProviderProps {
  /** Today's AOTD id, threaded into every play as `playContext.aotdId`. */
  aotdId: string | null;
  /** Gates the hand read — anonymous visitors never trigger the fetch. */
  isAuthenticated: boolean;
  children: React.ReactNode;
}

export default function AotdCardPlayProvider({
  aotdId,
  isAuthenticated,
  children,
}: AotdCardPlayProviderProps) {
  const [target, setTarget] = React.useState<CardInstance | null>(null);
  const [playability, setPlayability] = React.useState<Playability | null>(null);

  const { data: myCardsData } = useGetMyCardsQuery(undefined, {
    skip: !isAuthenticated,
  });

  const anonymousCardInHand = React.useMemo<CardInstance | null>(() => {
    for (const c of myCardsData?.cards ?? []) {
      if (c.status !== "in_hand") continue;
      const t = c.definition?.effect?.type;
      if (t === "comment_anonymize" || t === "anonymize") return c;
    }
    return null;
  }, [myCardsData?.cards]);

  const openCardPlay = React.useCallback(
    (card: CardInstance, play?: Playability | null) => {
      setTarget(card);
      setPlayability(play ?? null);
    },
    [],
  );

  const [playableFocusRequest, setPlayableFocusRequest] = React.useState(0);
  const focusPlayableCard = React.useCallback(
    () => setPlayableFocusRequest((n) => n + 1),
    [],
  );

  const value = React.useMemo(
    () => ({ openCardPlay, anonymousCardInHand, focusPlayableCard, playableFocusRequest }),
    [openCardPlay, anonymousCardInHand, focusPlayableCard, playableFocusRequest],
  );

  return (
    <AotdCardPlayContext.Provider value={value}>
      {children}
      <PlayCardModal
        card={target}
        open={Boolean(target)}
        playability={playability}
        playContext={aotdId ? { aotdId } : undefined}
        onOpenChange={(open) => {
          if (!open) {
            setTarget(null);
            setPlayability(null);
          }
        }}
        onPlayed={() => {
          setTarget(null);
          setPlayability(null);
        }}
      />
    </AotdCardPlayContext.Provider>
  );
}
